import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { SidebarGrid } from 'elements/minute/sidebar/sidebarComponents/complex/SidebarGrid/SidebarGrid';
import { debounce, noop, uniq } from 'lodash';
import { useCallback, useContext, useState } from 'react';
import Sketch from 'react-color/lib/Sketch';
import { CtxEdit } from 'session/ctxEdit';
import styled from 'styled-components/macro';
import { useUpdateEffect } from 'usehooks-ts';

export const DEFAULT_COLORS = [
    '#C5320F',
    '#1C4CB8',
    '#518F32',
    '#CEB10A',
    '#6A32AD',
    '#1E979E',
    '#A7018C',
    '#2F3B51',
    '#825C24',
    '#9E6615',
    '#C7C7C7',
    '#ffffff'
];

export const TRANSPARENT_COLOR = '#00000000';

interface Props {
    className?: string;
    transparentAllowed?: boolean;
    pickerAllowed?: boolean;
    colors?: string[];
    additionalColors?: string[];
    value: string;
    onChange?: (value: string) => void;
    highlightedColors?: string[];
}

export const ColorPalette = (props: Props): JSX.Element => {
    const ctxEdit = useContext(CtxEdit);
    const colors = uniq(
        (props.colors ?? DEFAULT_COLORS)
            .concat(props.additionalColors ?? [])
            .concat(props.highlightedColors ?? [])
    );
    const transparentAllowed = Boolean(props.transparentAllowed);
    const pickerAllowed = Boolean(props.pickerAllowed);
    const transparentIndex = transparentAllowed ? 0 : -1;
    const pickerIndex = pickerAllowed ? Number(transparentAllowed) : -1;

    const [currentValue, setCurrentValue] = useState(props.value);

    const colorsIndexStart = Number(transparentAllowed) + Number(pickerAllowed);

    const onChangeDebounced = useCallback(props.onChange ? debounce(props.onChange, 200) : noop, [
        props.onChange
    ]);

    useUpdateEffect(() => {
        if (currentValue !== props.value) {
            setCurrentValue(props.value);
        }
    }, [props.value]);

    useUpdateEffect(() => {
        if (currentValue !== props.value) {
            onChangeDebounced(currentValue);
            if (currentValue !== TRANSPARENT_COLOR) {
                ctxEdit.setMarkerDefault({ ...ctxEdit.markerDefault, color: currentValue });
            }
        }
    }, [currentValue]);

    const [displayColorPicker, setDisplayColorPicker] = useState(false);

    const getGridSelectedIndex = () => {
        if (currentValue === TRANSPARENT_COLOR) {
            return transparentIndex;
        }

        const index = colors.findIndex((c) => c === currentValue);
        if (index !== -1) {
            return index + colorsIndexStart;
        } else {
            return pickerIndex;
        }
    };

    const gridSelectedIndex = getGridSelectedIndex();

    const transparentIconElement = (
        <S_ItemContainer key={'transparent'}>
            <S_Transparent
                $isSelected={gridSelectedIndex === transparentIndex}
                icon={['fal', 'circle-half-stroke']}
            />
        </S_ItemContainer>
    );
    const paletteIconElement = (
        <S_ItemContainer key={'palette'}>
            <S_Palette icon={['fal', 'palette']} />
        </S_ItemContainer>
    );

    const colorElements = colors.map((color) => (
        <S_ItemContainer key={color}>
            <S_Color $color={color} />
            {props.highlightedColors?.includes(color) && <S_HighlightIcon icon={['fas', 'star']} />}
        </S_ItemContainer>
    ));
    const items = [
        transparentAllowed ? transparentIconElement : null,
        pickerAllowed ? paletteIconElement : null,
        ...colorElements
    ].filter(Boolean);

    return (
        <S_ColorPalette className={props.className}>
            <SidebarGrid
                items={items}
                columns={7}
                selectedIndex={gridSelectedIndex}
                onClick={(index) => {
                    if (index === transparentIndex) {
                        setCurrentValue(TRANSPARENT_COLOR);
                    } else if (index === pickerIndex) {
                        setDisplayColorPicker((curr) => !curr);
                    } else {
                        setCurrentValue(colors[index - colorsIndexStart]);
                    }
                }}
            />
            {displayColorPicker ? (
                <S_ColorPickerPopover>
                    <S_ColorPickerCover
                        onClick={() => {
                            setDisplayColorPicker(false);
                        }}
                    />
                    <Sketch
                        color={currentValue ?? '#000'}
                        presetColors={[]}
                        disableAlpha={true}
                        onChange={(color) => {
                            setCurrentValue(color.hex);
                        }}
                    />
                </S_ColorPickerPopover>
            ) : null}
        </S_ColorPalette>
    );
};

const S_ColorPalette = styled.div`
    position: relative;
`;

const S_Transparent = styled(FontAwesomeIcon)<{ $isSelected: boolean }>`
    width: 20px;
    height: 20px;
    font-size: 20px;
    color: #141c54;
    transform: rotate(315deg);
    cursor: ${(props) => (props.$isSelected ? 'default' : 'pointer')};
`;

const S_ItemContainer = styled.div`
    padding: 9px;
    position: relative;
`;

const S_Palette = styled(FontAwesomeIcon)`
    width: 20px;
    height: 20px;
    font-size: 20px;
    cursor: pointer;
    color: #141c54;
`;

const S_Color = styled.div<{ $color: string }>`
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: ${(props) => props.$color};
    box-shadow: 0px -1px 0px 0px rgba(0, 0, 0, 0.3) inset, 0px 1px 5px 0px rgba(0, 0, 0, 0.07);
    padding: 9px;
`;

const S_ColorPickerPopover = styled.div`
    position: absolute;
    z-index: 9999;
    top: 41px;
`;

const S_ColorPickerCover = styled.div`
    position: fixed;
    top: 0px;
    right: 0px;
    bottom: 0px;
    left: 0px;
`;

const S_HighlightIcon = styled(FontAwesomeIcon)`
    width: 10px;
    height: 10px;
    color: #000;
    position: absolute;
    right: 2px;
    bottom: 2px;
`;
